import React, { Component } from 'react';
import {
    Grid,
    Card,
    Toolbar,
    CardContent,
    Checkbox,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    RadioGroup,
    Radio,
    FormControlLabel,
    TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import Config from '../../config';
import Helpers, { parseSignalrMessage } from '../../helpers';
import Moment from 'react-moment';
import EnhancedTable from '../../components/table';
import Auth from '../../auth';
import Status from '../../components/status';
import { Timeline, TimelineEvent } from 'react-event-timeline';
import PostIcon from '@mui/icons-material/ChatBubble';
import ChangeIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import { SimpleRte } from '../../components/EnhancedRte';
import DocumentLink from '../../components/document_link';

interface PostData {
    Id: number;
    ThreadId?: number;
    Sender?: number;
    SenderName?: string;
    Receiver?: number;
    ReceiverName?: string;
    Author?: string;
    Body?: string;
    Date?: string;
    Category?: string;
    Status?: string;
    ExternalEmails?: string;
    [key: string]: unknown;
}

interface PostProps {
    coid?: number;
    post: PostData;
    reply: (postId?: number, threadId?: number) => void;
    contentStyle?: React.CSSProperties;
    eventStyle?: React.CSSProperties;
    // `Post` is a shared export; consumers (e.g. product_details) pass extra
    // presentational props such as `sx` that the component forwards/ignores.
    sx?: unknown;
    [key: string]: unknown;
}

const PostContent = styled('div')({
    "& p": {
        margin: 0
    },
    fontWeight: 400
});

class Post extends Component<PostProps> {
    render(){
        let {coid, post, reply, contentStyle} = this.props;
        if(post.Category === 'Status'){
            return <TimelineEvent
              title={<>{`${post.Author} from `}<b>{`${post.SenderName}`}</b>{` ${post.Body}`}</>}
              contentStyle={contentStyle || {boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)', borderRadius: '4px'}}
              createdAt={<Moment format='MM/DD/YYYY hh:mm A'>{post.Date}</Moment>}
              icon={<ChangeIcon style={{fontSize: '20px' }}/>}
              iconColor="#737373"
            >
            </TimelineEvent>;
        }
        if(post.Category === 'CC'){
            return <TimelineEvent
              title={<>{`${post.Author} from `}<b>{`${post.SenderName}`}</b>{` ${post.Body}`}</>}
              contentStyle={contentStyle || {boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)', borderRadius: '4px'}}
              createdAt={<Moment format='MM/DD/YYYY hh:mm A'>{post.Date}</Moment>}
              icon={<EmailIcon style={{fontSize: '20px' }}/>}
              iconColor="#737373"
            >
            </TimelineEvent>;
        }
        return <TimelineEvent
          title={<>{`${post.Author} from `}<b>{`${post.SenderName}`}</b></>}
          contentStyle={contentStyle || {boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)', borderRadius: '4px'}}
          createdAt={<Moment format='MM/DD/YYYY hh:mm A'>{post.Date}</Moment>}
          icon={<PostIcon style={{fontSize: '20px', transform: post.Sender === coid ? 'scaleX(-1)' : undefined }}/>}
          buttons={post.Sender !== coid ? <Button variant='text' style={{fontSize: '12px', minHeight: 0, padding: '5px 10px', bottom: '10px'}} aria-label="Reply" onClick={() => reply(post.Id, post.ThreadId)}>Reply</Button> : undefined}
          iconColor="#737373"
        >
        <PostContent style={this.props.eventStyle} dangerouslySetInnerHTML={{__html: post.Body}}></PostContent>
        </TimelineEvent>;
    }
}

const StyledPost = Post;

const statusColors: Record<string, string> = {
    "New": "#275E7A",
    "In Progress": "#ffa500",
    "Complete": "#448044",
    "Blocked": "#ee3224"
};

const postStatuses = Object.keys(statusColors);
// All statuses are writable — the backend terminal status is 'Complete', and
// there is no display-only status to gate. writeStatuses is kept as a distinct
// alias for the write paths (ChangeStatusModal, New Message) so a display-only
// status could be reintroduced later without re-threading every consumer.
const writeStatuses = [...postStatuses];

interface StatusProps {
    status: string;
}

class PostStatusChip extends Component<StatusProps> {
    render(){
        let {status} = this.props;
        let color = statusColors[status];
        return <Status color={color} description={status}/>;
    }
}

class PostStatusBar extends Component<StatusProps>{
    render(){
        let {status} = this.props;
        let color = statusColors[status];
        return <Toolbar className='lbstatusbar' style={{backgroundColor: color}}>Status: {status}</Toolbar>;
    }
}

interface StatusModalProps {
    open: boolean;
    onClose: () => void;
    value: string;
    onChange?: (value: string) => void;
    submit: () => void;
    disabled?: boolean;
}

class ChangeStatusModal extends Component<StatusModalProps>{
    render(){
        let {open, onClose, value, onChange, submit, disabled} = this.props;
        return <Dialog maxWidth='sm' fullWidth open={open} onClose={onClose}>
        <Toolbar className='lbtoolbar'>Update Status</Toolbar>
        <DialogContent>
        <RadioGroup value={value} onChange={(e) => onChange && onChange(e.target.value)}>
            <FormControlLabel value='' control={<Radio />} label='None'/>
            {writeStatuses.map(r => <FormControlLabel key={r} value={r} control={<Radio />} label={r}/>)}
        </RadioGroup>
        </DialogContent>
        <DialogActions>
        <Button variant='text' onClick={submit} disabled={disabled} color="primary">
          Update
        </Button>
      </DialogActions>
    </Dialog>;
    }
}

interface ExternalEmailModalProps {
    open: boolean;
    onClose: () => void;
    value: string;
    onChange: (value: string) => void;
    submit: () => void;
    disabled?: boolean;
}

class ExternalEmailModal extends Component<ExternalEmailModalProps>{
    render(){
        let {open, onClose, value, onChange, submit, disabled} = this.props;
        return <Dialog maxWidth='sm' fullWidth open={open} onClose={onClose}>
        <Toolbar className='lbtoolbar'>Edit CC</Toolbar>
        <DialogContent style={{display: 'flex'}}>
            <TextField label='CC Email' style={{flex: 1}} value={value} onChange={e => onChange(e.currentTarget.value)} disabled={disabled}/>
        </DialogContent>
        <DialogActions>
        <Button variant='text' onClick={submit} disabled={disabled} color='primary'>
          Update
        </Button>
      </DialogActions>
    </Dialog>;
    }
}

interface ThreadTopPost {
    Subject?: string;
    Status?: string;
    DocId?: number | string;
    DocumentType?: string;
    SenderName?: string;
    ExternalEmails?: string;
    Body?: string;
    Sender?: number;
    RequireAck?: boolean;
    Acknowledged?: boolean;
    AcknowledgedBy?: string;
    AckDate?: string;
    [key: string]: unknown;
}

interface ThreadReceiptDetail {
    Receiver?: number;
    ReceiverName?: string;
    [key: string]: unknown;
}

interface ThreadDetails {
    TopPost: ThreadTopPost;
    ReceiptDetails: ThreadReceiptDetail[];
    [key: string]: unknown;
}

interface SignalrConnection {
    on: (event: string, handler: (message: unknown) => void) => void;
    off: (event: string, handler: (message: unknown) => void) => void;
}

interface ThreadProps {
    setTitle?: (title: string, hideEnvironment?: boolean) => void;
    signalr?: SignalrConnection;
}

interface ThreadState {
    posts: PostData[];
    showReply: boolean;
    disableReply: boolean;
    showChangeStatus: boolean;
    showExternalEmail: boolean;
    thread?: ThreadDetails;
    accountNumber?: number;
    threadId?: string;
    isReceiver?: boolean;
    currentPost?: number;
    reply?: string;
    replyEmpty?: boolean;
    updateStatus?: string;
    externalEmails?: string;
    disableUpdate?: boolean;
}

class Thread extends Component<ThreadProps, ThreadState> {
    state: ThreadState = {posts: [], showReply: false, disableReply: false, showChangeStatus: false, showExternalEmail: false};
    componentDidMount(){
        const threadId = new Helpers().queryString()["id"];
        if (this.props.setTitle && threadId) {
            this.props.setTitle(`Message: ${threadId}`, true);
        }
        this.loadData();
        if(this.props.signalr){
            this.props.signalr.on('notify', this.handler);
        }
    }
    async componentDidUpdate(prevProps: ThreadProps) {
        if(!prevProps.signalr && this.props.signalr){
            this.props.signalr.on('notify', this.handler);
        }
    }
    componentWillUnmount(){
        if(this.props.signalr){
            this.props.signalr.off('notify', this.handler);
        }
    }
    handler = (rawMessage: unknown) => {
        const message: any = parseSignalrMessage(rawMessage);
        if(message && message.action === 'newThreadMessage' && message.additionalData && message.additionalData.ThreadId === parseInt(this.state.threadId)){
            let posts = this.state.posts;
            if(posts.filter(r => r.Id === message.additionalData.Id).length > 0){
                return;
            }
            posts.push(message.additionalData);
            let newState: Pick<ThreadState, 'posts'> & { thread?: ThreadDetails } = {posts};
            if(this.state.thread && this.state.thread.TopPost && message.additionalData.Category === 'Status' && this.state.thread.TopPost.Status !== message.additionalData.Status){
                let topPost = {...this.state.thread.TopPost};
                topPost.Status = message.additionalData.Status;
                let thread = {...this.state.thread};
                thread.TopPost = topPost;
                newState.thread = thread;
            }
            if(this.state.thread && this.state.thread.TopPost && message.additionalData.Category === 'CC' && this.state.thread.TopPost.ExternalEmails !== message.additionalData.ExternalEmails){
                let topPost = {...this.state.thread.TopPost};
                topPost.ExternalEmails = message.additionalData.ExternalEmails;
                let thread = {...this.state.thread};
                thread.TopPost = topPost;
                newState.thread = thread;
            }
            this.setState(newState);
        }
    }
    loadData = async () => {
        let threadid = new Helpers().queryString()["id"];
        let threadDetails = (await axios.get(Config.api + `/odata/company/Functions.GetThreadDetails?threadid=${threadid}`)).data
        let accountNumber = parseInt(Auth.getAccountNumber());
        this.setState({thread: threadDetails, accountNumber: accountNumber, threadId: threadid, isReceiver: accountNumber !== threadDetails.TopPost.Sender});
        let posts = (await axios.get(Config.api + `/odata/Company/VwCompanyPosts?$select=Id,Sender,SenderName,Receiver,ReceiverName,Body,Date,Author,Category&$filter=ThreadId eq ${threadid}&$orderby=Date desc`)).data.value;
        this.setState({posts: posts});
    }
    getReceiverData = () => {
        return this.state.thread.ReceiptDetails;
    }
    reply = (postId?: number) => {
        this.setState({showReply: true, currentPost: postId, reply: '', replyEmpty: true});
    }
    sendPost = async () => {
        this.setState({disableReply: true});
        var html = this.state.reply;
        try{
            await axios.post(Config.api + '/odata/Company/Functions.NewPost', {
                ReplyTo: this.state.currentPost,
                ThreadId: this.state.threadId,
                Body: html
            });
            this.setState({showReply: false, disableReply: false});
            this.loadData();
        } catch(e) {
            this.setState({disableReply: false});
        }
    }
    acknowledge = async () => {
        await axios.post(Config.api + '/odata/Company/Functions.AcknowledgePost', {ReplyTo: this.state.threadId});
        this.loadData();
    }
    sortPosts(posts: PostData[]){
        // sort descending
        return posts.sort(function(a, b){
          return new Date(b.Date as string).getTime() - new Date(a.Date as string).getTime();
        });
    }
    showChangeStatus = () => {
        this.setState({showChangeStatus: true, updateStatus: this.state.thread.TopPost.Status || '', disableUpdate: false});
    }
    showUpdateExternalEmail = () => {
        this.setState({showExternalEmail: true, externalEmails: this.state.thread.TopPost.ExternalEmails || '', disableUpdate: false});
    }
    updateStatus = async () => {
        this.setState({disableUpdate: true});
        try{
            await axios.post(Config.api + '/odata/Company/Functions.UpdatePostStatus', {ThreadId: this.state.threadId, Status: this.state.updateStatus});
        }catch{

        }
        this.setState({disableUpdate: false, showChangeStatus: false});
    }
    updateExternalEmails = async () => {
        this.setState({disableUpdate: true});
        try{
            await axios.post(Config.api + '/odata/Company/Functions.UpdateExternalEmails', {ThreadId: this.state.threadId, ExternalEmails: this.state.externalEmails});
        }catch{

        }
        this.setState({disableUpdate: false, showExternalEmail: false});
    }
    render() {
        let config = {
            getData: this.getReceiverData,
            columns: [
              { id: "Id", hidden: true },
              { id: 'ReceiverName', filterable: true, sortable: true, label: 'Recipient'},
              { id: 'Acknowledged', filterable: true, sortable: true, label: 'Acknowledged', width: '15em', template: (value: any) => <Checkbox checked={value}/>},
              { id: 'AckDate', type: 'date', filterable: true, sortable: true, label: 'Date', template: (value: any) => (value ? <Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment> : ''), width: '15em' },
              { id: 'AckBy', filterable: true, sortable: true, label: 'Acknowledged By', width: '15em'},
          ],
          order: 'asc',
          orderBy: 'ReceiverName',
          keyField: 'Id',
          pageSize: 20,
          pageSizes: [10, 20, 50, 100],
          signalr: this.props.signalr,
          onSignalrUpdate: (rawMessage: unknown, page: any[], signalrOpts?: unknown) => {
              const message: any = parseSignalrMessage(rawMessage);
              if(message.action !== 'ackThread' || !message.additionalData || !message.additionalData.Id || message.additionalData.Id.toString() !== this.state.threadId){
                  return null;
              }
              var item = page.find((r: ThreadReceiptDetail) => r.Receiver === message.partnerCoId);
              if(!item){
                  return null;
              }
              return { Id: item.Id, Acknowledged: true, AckDate: message.additionalData.AckDate, AckBy: message.additionalData.AcknowledgedBy };
          },
          title: 'Acknowledgements'
        };
        if(this.state.thread){
            return <Grid container spacing={2}>
                <Grid size={12}>
                    <Card>
                        <Toolbar className='lbtoolbar'>
                            <span style={{flex: 1, textAlign: 'left'}}>{this.state.thread.TopPost.Subject}</span>
                            <Button variant="text" style={{color: 'white'}} onClick={this.showUpdateExternalEmail}>Edit CC</Button>
                            <Button variant="text" style={{color: 'white'}} onClick={this.showChangeStatus}>Change Status</Button>
                        </Toolbar>
                        {!(!this.state.thread.TopPost.Status) && <PostStatusBar status={this.state.thread.TopPost.Status}/>}
                        {!(!this.state.thread.TopPost.DocId) ? <Toolbar style={{fontWeight: 'bold', minHeight: '50px', lineHeight: '50px'}}><span>Linked document: <DocumentLink docType={this.state.thread.TopPost.DocumentType} id={this.state.thread.TopPost.DocId}>{this.state.thread.TopPost.DocId}</DocumentLink></span></Toolbar> : ''}
                        <PostContent as={CardContent} style={{textAlign: 'left'}}>
                            {this.state.isReceiver && <div>From: {this.state.thread.TopPost.SenderName}</div>}
                            {!this.state.isReceiver && <div>To: {this.state.thread.ReceiptDetails.map(r => r.ReceiverName).join(", ")}</div>}
                            {!(!this.state.thread.TopPost.ExternalEmails) && <div>CC: {this.state.thread.TopPost.ExternalEmails}</div>}
                            <div dangerouslySetInnerHTML={{__html: this.state.thread.TopPost.Body}}></div>
                        </PostContent>
                    </Card>
                </Grid>
                {!this.state.isReceiver && this.state.thread.TopPost.RequireAck ?
                    <Grid size={12}>
                        <EnhancedTable config={config}/>
                    </Grid>
                    : ''}
                {this.state.isReceiver && this.state.thread.TopPost.Acknowledged?
                    <Grid size={12}>
                        <Card>
                            <CardContent style={{textAlign: 'left'}}>Acknowledged by {this.state.thread.TopPost.AcknowledgedBy} on {<Moment format='MM/DD/YYYY hh:mm A'>{this.state.thread.TopPost.AckDate}</Moment>}</CardContent>
                        </Card>
                    </Grid>
                     : ''}
                <Grid size={12} style={{textAlign: 'left'}}>
                    {this.state.isReceiver && <Button color='primary' variant='contained' onClick={() => this.reply()}>Reply</Button>}
                    {this.state.isReceiver && !this.state.thread.TopPost.Acknowledged && this.state.thread.TopPost.RequireAck ?
                        <Button style={{marginLeft: '1em'}} color='primary' variant='contained' onClick={() => this.acknowledge()}>Acknowledge</Button>
                         : ''}
                </Grid>
                {this.state.posts.length ?
                    <Grid size={12}>
                        <Timeline style={{fontSize: '100%'}}>
                        {this.sortPosts(this.state.posts).map(post =>
                            <StyledPost key={post.Id} post={post} coid={this.state.accountNumber} reply={this.reply}/>
                        )}
                        </Timeline>
                    </Grid>
                : ''}
                <Dialog maxWidth='md' fullWidth open={this.state.showReply} onClose={() => this.setState({showReply: false})}>
                    <Toolbar className='lbtoolbar'>New Message</Toolbar>
                    <DialogContent style={{height: '300px', display: 'flex'}}>
                    <SimpleRte className='textEditor' value={this.state.reply} disabled={this.state.disableReply} onChange={(html, isEmpty) => this.setState({reply: html, replyEmpty: isEmpty})}/>
                    </DialogContent>
                    <DialogActions>
                    <Button onClick={this.sendPost} disabled={this.state.replyEmpty || this.state.disableReply} color="primary">
                      Send
                    </Button>
                  </DialogActions>
                </Dialog>
                <ChangeStatusModal
                    open={this.state.showChangeStatus}
                    onClose={() => this.setState({showChangeStatus: false})}
                    value={this.state.updateStatus}
                    onChange={v => this.setState({updateStatus: v})}
                    submit={this.updateStatus}
                    disabled={this.state.disableUpdate}
                />
                <ExternalEmailModal
                    open={this.state.showExternalEmail}
                    onClose={() => this.setState({showExternalEmail: false})}
                    value={this.state.externalEmails}
                    onChange={v => this.setState({externalEmails: v})}
                    submit={this.updateExternalEmails}
                    disabled={this.state.disableUpdate}
                />
            </Grid>;
        }
        return <div/>;
    }
}

export default Thread;

export {StyledPost as Post, PostStatusChip, postStatuses, writeStatuses, ChangeStatusModal};
